import { useState } from "react";
import { Check, Trophy, Youtube, Flame } from "lucide-react";
import { useUnit } from "../context/UnitContext";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

const WARMUP_STEPS = [[0.4, 12], [0.6, 8], [0.8, 4]];

const WarmupSuggestion = ({ ex, workLb }) => {
  const { unit, dw } = useUnit();
  const [open, setOpen] = useState(false);
  const step = unit === "kg" ? 2.5 : 5;
  const rows = WARMUP_STEPS.map(([pct, reps]) => {
    const w = Math.max(step, Math.round(dw(workLb * pct) / step) * step);
    return { pct: Math.round(pct * 100), w, reps };
  });
  return (
    <div className="mt-1.5">
      <button data-testid={`warmup-toggle-${slug(ex.exercice)}`} onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-400/90 hover:text-amber-300 transition-colors">
        <Flame size={12} /> Échauffement suggéré {open ? "▾" : "▸"}
      </button>
      {open && (
        <div data-testid={`warmup-sets-${slug(ex.exercice)}`} className="mt-1 flex gap-1.5">
          {rows.map((r) => (
            <div key={r.pct} className="flex-1 bg-[#14141A] border border-amber-400/20 px-2 py-1.5 text-center">
              <p className="text-white text-xs font-mono">{r.w} {unit} × {r.reps}</p>
              <p className="text-[#B0B0B4] text-[9px]">{r.pct}%</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SetRow = ({ ex, num, logged, prevSet, onValidate }) => {
  const { unit, dw, toLb } = useUnit();
  const [poids, setPoids] = useState("");
  const [reps, setReps] = useState("");
  const [rpe, setRpe] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const id = `${slug(ex.exercice)}-set-${num}`;

  if (logged) {
    return (
      <div data-testid={`${id}-logged`} className="flex items-center gap-2 py-2 border-b border-white/5 text-sm">
        <span className="w-7 text-white text-[20px] font-bold font-mono shrink-0">{num}</span>
        <span className="text-white font-medium">{logged.poids ? `${dw(logged.poids)} ${unit}` : "PDC"} × {logged.reps}</span>
        {logged.rpe ? <span className="text-[#B0B0B4] text-xs">RPE {logged.rpe}</span> : null}
        {logged.prs?.length > 0 && (
          <span className="flex items-center gap-1 bg-[#FF3B30] text-white text-[9px] font-bold uppercase px-1.5 py-0.5">
            <Trophy size={10} /> PR
          </span>
        )}
        {logged.pending && <span className="text-amber-400 text-[9px] uppercase tracking-wider border border-amber-400/40 px-1">à sync</span>}
        {logged.notes && <span className="text-[#C8C8D0] text-xs truncate flex-1">{logged.notes}</span>}
        <span className="h-[60px] w-[60px] rounded-[10px] flex items-center justify-center bg-[#39FF14] text-black ml-auto shrink-0 shadow-[0_0_18px_rgba(57,255,20,0.7)]">
          <Check size={30} strokeWidth={3.5} />
        </span>
      </div>
    );
  }

  const submit = async () => {
    if (!reps) return;
    setSaving(true);
    await onValidate(num, {
      poids: poids === "" ? null : toLb(parseFloat(poids)),
      reps: parseInt(reps, 10),
      rpe: rpe === "" ? null : parseFloat(rpe),
      notes: notes || null,
    });
    setSaving(false);
  };

  return (
    <div className="py-2 border-b border-white/5">
      <div className="flex items-center gap-1.5">
        <span className="w-7 text-white text-[22px] font-black font-mono shrink-0">{num}</span>
        <input data-testid={`${id}-poids-input`} type="number" inputMode="decimal"
          placeholder={prevSet ? (prevSet.poids != null ? String(dw(prevSet.poids)) : "PDC") : unit}
          className={`set-input w-[80px] ${prevSet ? "prefilled" : ""}`} value={poids} onChange={(e) => setPoids(e.target.value)} />
        <input data-testid={`${id}-reps-input`} type="number" inputMode="numeric"
          placeholder={prevSet ? String(prevSet.reps) : "reps"}
          className={`set-input w-[72px] ${prevSet ? "prefilled" : ""}`} value={reps} onChange={(e) => setReps(e.target.value)} />
        <input data-testid={`${id}-rpe-input`} type="number" inputMode="decimal" placeholder="RPE" min="1" max="10"
          className="set-input w-[64px]" value={rpe} onChange={(e) => setRpe(e.target.value)} />
        <button data-testid={`${id}-validate-button`} onClick={submit} disabled={!reps || saving}
          className="ml-auto h-[60px] w-[60px] rounded-[10px] shrink-0 flex items-center justify-center bg-[#00E5FF] text-black shadow-[0_0_16px_rgba(0,229,255,0.6)] active:scale-95 transition-transform disabled:pointer-events-none">
          <Check size={30} strokeWidth={3.5} />
        </button>
      </div>
      <input data-testid={`${id}-notes-input`} placeholder="Notes (optionnel)"
        className="notes-input w-full mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} />
    </div>
  );
};

export const ExerciseCard = ({ ex, loggedSets, onValidate, prev }) => {
  const isInfo = ex.muscle === "Cardio" || ex.muscle === "Mobilité";
  const picto = ex.picto_url ? `${process.env.REACT_APP_BACKEND_URL}${ex.picto_url}` : null;
  if (isInfo) {
    return (
      <div className="bg-[#14141A] border border-[#2E2E3A] px-4 py-3 flex items-center gap-3">
        {picto && <img src={picto} alt={ex.exercice} className="w-12 h-12 object-cover bg-white shrink-0" />}
        <div className="flex-1">
          <p className="text-white text-sm font-medium">{ex.exercice}</p>
          <p className="text-[#B0B0B4] text-xs">{ex.groupe} · {ex.objectif}</p>
        </div>
        {ex.video_url && (
          <a data-testid={`video-link-${slug(ex.exercice)}`} href={ex.video_url} target="_blank" rel="noreferrer"
            className="text-[#B0B0B4] hover:text-[#FF3B30] transition-colors p-1"><Youtube size={16} /></a>
        )}
      </div>
    );
  }
  const nbSeries = ex.series || 4;
  const done = loggedSets.length >= nbSeries;
  const workLb = Math.max(...(prev?.sets?.map((s) => s.poids || 0) || [0]));
  return (
    <div data-testid={`exercise-card-${slug(ex.exercice)}`}
      className={`bg-[#14141A] border ${done ? "border-[#39FF14]/70" : "border-[#2E2E3A]"} px-4 py-3`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-start gap-3">
          {picto && (
            <img data-testid={`picto-${slug(ex.exercice)}`} src={picto} alt={ex.exercice}
              className="w-14 h-14 object-cover bg-white shrink-0 border border-[#2E2E3A]" />
          )}
          <div>
          <div className="flex items-center gap-2">
            <h3 className="text-white font-black text-[20px] leading-tight">{ex.exercice}</h3>
            {ex.video_url && (
              <a data-testid={`video-link-${slug(ex.exercice)}`} href={ex.video_url} target="_blank" rel="noreferrer"
                className="text-[#B0B0B4] hover:text-[#FF3B30] transition-colors shrink-0"><Youtube size={16} /></a>
            )}
          </div>
          <p className="text-[#C8C8D0] text-[15px] mt-0.5">{ex.muscle} · {ex.equipement} · Tempo {ex.tempo}</p>
          {prev && <p className="text-[10px] text-[#32D74B]/80 mt-0.5">Dernière séance ({prev.date}) pré-remplie en gris</p>}
          </div>
        </div>
        <span className="text-[14px] font-black uppercase text-black bg-[#FFE600] px-3 py-2 rounded-[6px] shadow-[0_0_14px_rgba(255,230,0,0.4)] shrink-0 ml-2">
          {ex.objectif}
        </span>
      </div>
      {workLb >= 30 && !done && <WarmupSuggestion ex={ex} workLb={workLb} />}
      <div className="mt-2">
        {Array.from({ length: nbSeries }, (_, i) => i + 1).map((num) => (
          <SetRow key={num} ex={ex} num={num} logged={loggedSets[num - 1]}
            prevSet={prev?.sets?.find((s) => s.serie_num === num)}
            onValidate={(n, vals) => onValidate(ex, n, vals)} />
        ))}
      </div>
    </div>
  );
};
